import { Divider } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useMemo, useState } from 'react';
import { useMatch } from 'react-router';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  BaseThreadFragmentFragmentGQLType,
  GetEventLogsQueryGQLType,
  ThreadActionGQLType,
  DeliverableEntityMetaFragmentFragmentGQLType,
  EventLogTypeGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { EventLogExportDataRow } from 'packages/event-logs/types';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { PermissionsFn } from '~/core/PermissionGuard';
import * as CreditFacilityEntityConfig from '~/core/routes/credit-facilities/EntityConfig';
import { Thread } from '~/packages/comments/Thread';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM } from '~/packages/event-logs/constants';
import { EntityName as BaseEntityName } from '~/packages/event-logs/EntityName';
import {
  EventLogFilter,
  useEventLogFilters,
} from '~/packages/event-logs/EventLogFilters';
import { basicStringSearch } from '~/packages/event-logs/helpers';
import { useGetDeliverableFiltersQuery } from '~/packages/event-logs/queries/entityFilters.gql.generated';

// Constants
export const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'deliverable_container_thread_create',
];

// Types
export type EntityMeta = DeliverableEntityMetaFragmentFragmentGQLType;
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];
type ThreadData = BaseThreadFragmentFragmentGQLType;

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';
  return context.flags.productsDeliverablesPermissions;
};

type EntityNameProps = Pick<
  EntityMeta,
  'deliverableId' | 'configName' | 'latestReceiptId'
> & {
  dueDate?: Util.FullDateStr;
};
export const EntityName = ({
  deliverableId,
  configName,
  dueDate,
  latestReceiptId,
}: EntityNameProps) => {
  const { user } = useUserAuthOrThrow();
  const match = useMatch({ path: '/deliverables/:deliverableId' });

  const isOnDeliverableRoute = match?.params.deliverableId === deliverableId;

  const name = useMemo(() => {
    const configLabel = commonUtils.toTitleCase(configName);
    if (!dueDate) return configLabel;
    const dateLabel = commonDate.fullDateStrToShortMonthDayString(dueDate);
    return `${configLabel} - ${dateLabel}`;
  }, [configName, dueDate]);

  return (
    <BaseEntityName
      isOnEntityRoute={isOnDeliverableRoute}
      label={name}
      tooltipLabel={`Deliverable ID: ${deliverableId}`}
      to={
        latestReceiptId && user.userType === 'CAPITAL_PROVIDER'
          ? `/deliverables/${deliverableId}/final-output-report-versions/${latestReceiptId}?${EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM}`
          : `/deliverables/${deliverableId}?${EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM}`
      }
    />
  );
};

type ThreadFeedItemProps = {
  thread: ThreadData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const ThreadFeedItem = ({
  thread,
  onThreadAction,
  onMount,
  onReady,
}: ThreadFeedItemProps) => {
  switch (thread.domain) {
    case 'deliverables_container': {
      return (
        <Thread
          key={thread.threadId}
          threadId={thread.threadId}
          onThreadAction={onThreadAction}
          onReady={() => onReady?.(thread.threadId)}
          onMount={() => onMount?.(thread.threadId)}
        >
          <Typography inline variant="detail-muted">
            Thread on{' '}
            <EntityName
              deliverableId={thread.domainIdentifier}
              configName="Deliverable"
              // !TODO: switch to entity metadata
              // configName={thread.entityMetadata.configName}
              // dueDate={thread.entityMetadata.dueDate}
            />{' '}
            {/* !TODO: switch to entity metadata */}
            {/* for{' '} */}
            {/* <CreditFacilityEntityConfig.EntityName {...thread.creditFacility} /> */}
          </Typography>
          <Divider />
        </Thread>
      );
    }
    default: {
      return null;
    }
  }
};

type EventFeedItemProps = {
  log: EventLogData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const EventFeedItem = ({
  log,
  onThreadAction,
  onMount,
  onReady,
}: EventFeedItemProps) => {
  switch (log.__typename) {
    case 'DeliverableThreadCreateEvent': {
      return (
        <Thread
          key={log.eventId}
          threadId={log.current.id}
          onThreadAction={onThreadAction}
          onMount={() => onMount?.(log.current.id)}
          onReady={() => onReady?.(log.current.id)}
        >
          <Typography inline variant="detail-muted">
            Thread on <EntityName {...log.entityMetadata} /> for{' '}
            <CreditFacilityEntityConfig.EntityName {...log.creditFacility} />
          </Typography>
          <Divider />
        </Thread>
      );
    }

    default: {
      return null;
    }
  }
};

export const EntityFilter = () => {
  const selectedIds = useEventLogFilters((s) => s.deliverableIds);
  const onSelect = useEventLogFilters((s) => s.setDeliverableIds);

  const [searchString, setSearchString] = useState<string>('');

  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );
  const entityFiltersQuery = useGetDeliverableFiltersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      creditFacilityIds,
      // !TODO if we want more, we should allow users to search for entity by string
      limit: 25,
    },
  });

  const entities: EntityMeta[] = useMemo(
    () =>
      entityFiltersQuery.data?.items.map((entity) => ({
        ...entity,
        __typename: 'DeliverableEntityMeta',
      })) ?? [],
    [entityFiltersQuery.data?.items],
  );

  const options = useMemo(
    () =>
      entities
        .map((entity) => {
          const dateLabel = commonDate.fullDateStrToShortMonthDayString(
            entity.dueDate,
          );
          const textLabel = `${entity.configName} - ${dateLabel}`;
          return {
            id: entity.deliverableId,
            textLabel,
            data: entity,
          };
        })
        .filter((option) => basicStringSearch(searchString, option.textLabel)),
    [entities, searchString],
  );

  if (entityFiltersQuery.error) {
    Sentry.captureException(entityFiltersQuery.error);
    return null;
  }

  if (entities.length < 1) return null;

  return (
    <EventLogFilter
      onSearchInputChange={setSearchString}
      leftIcon={<Icon icon="calendar" />}
      noneLabel="Any Deliverable"
      singularLabel="Deliverable"
      pluralLabel="Deliverables"
      selectedIds={selectedIds}
      options={options}
      onClose={onSelect}
    />
  );
};

/* eslint-disable camelcase */
export const toExportDataRow = (
  dataRow: EventLogExportDataRow,
  log: EventLogData,
): EventLogExportDataRow => {
  switch (log.__typename) {
    case 'DeliverableThreadCreateEvent': {
      const nextDataRow = {
        ...dataRow,
        deliverable_meta_configName: log.entityMetadata.configName,
        deliverable_meta_dueDate: log.entityMetadata.dueDate,
      };

      if (log.entityMetadata.latestReceiptId) {
        nextDataRow.deliverable_meta_latestReceiptId =
          log.entityMetadata.latestReceiptId;
      }

      return nextDataRow;
    }

    default: {
      return dataRow;
    }
  }
};
/* eslint-enable camelcase */
